import React, { useState } from "react";

const FlutterApp = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Learn to build
          cross-platform mobile applications using Flutter and Dart.
          <p className="font-bold text-sm">Duration:</p> 45 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic knowledge of
          programming (e.g., JavaScript, Python), familiarity with
          object-oriented concepts.
          <p className="font-bold text-sm">Tools Needed:</p> Flutter SDK, Dart,
          Android Studio or VS Code, an emulator or physical device, and a code
          editor.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to Flutter",
          objective: "Understand the basics of Flutter and its ecosystem.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is Flutter? Why use it?</li>
                <li>Flutter vs. other frameworks (React Native, Xamarin).</li>
                <li>Setting up the Flutter environment.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install Flutter and run a sample app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Dart Basics",
          objective: "Learn the fundamentals of Dart programming.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Variables, data types, and operators.</li>
                <li>Control structures (if-else, loops).</li>
                <li>Functions and basic input/output.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a simple Dart script with loops.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Dart Object-Oriented Programming",
          objective: "Master OOP concepts in Dart.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Classes, objects, and inheritance.</li>
                <li>Interfaces and abstract classes.</li>
                <li>Constructors and getters/setters.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a class with inheritance in Dart.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Flutter Widgets Basics",
          objective: "Understand the widget system in Flutter.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What are widgets? Stateless vs. Stateful.</li>
                <li>Common widgets: Text, Container, Row, Column.</li>
                <li>Building a simple UI layout.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a basic Flutter app with Text and Container.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Layout and Styling",
          objective: "Learn layout management and styling in Flutter.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using Padding, Margin, and Alignment.</li>
                <li>Applying colors, fonts, and themes.</li>
                <li>Responsive design with MediaQuery.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Style a Flutter layout with custom themes.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Navigation in Flutter",
          objective: "Implement navigation between screens.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Navigator and routes.</li>
                <li>Passing data between screens.</li>
                <li>Named routes and the Navigator 2.0.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a multi-screen app with navigation.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Handling User Input",
          objective: "Manage user input with forms and gestures.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>TextField and Form widgets.</li>
                <li>Validation and submission.</li>
                <li>GestureDetector and InkWell.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a form with input validation.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: State Management Basics",
          objective: "Understand state management in Flutter.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is state? setState usage.</li>
                <li>Lifting state up.</li>
                <li>Introduction to Provider.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Manage state with setState in a counter app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Advanced State Management with Provider",
          objective: "Use Provider for state management.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up Provider in Flutter.</li>
                <li>ChangeNotifier and Consumer.</li>
                <li>Sharing state across widgets.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement a Provider-based state management app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Introduction to BLoC Pattern",
          objective: "Learn the BLoC pattern for state management.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is BLoC? Streams and sinks.</li>
                <li>Setting up the flutter_bloc package.</li>
                <li>Basic BLoC implementation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a simple app with BLoC.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Networking with HTTP",
          objective: "Fetch data from APIs in Flutter.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using the http package.</li>
                <li>Making GET and POST requests.</li>
                <li>Handling JSON data.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Fetch data from a public API (e.g., JSONPlaceholder).</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Working with REST APIs",
          objective: "Manage RESTful services in Flutter.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding REST architecture.</li>
                <li>Handling authentication and errors.</li>
                <li>Using Dio for advanced networking.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build an app to interact with a REST API.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: Local Storage with SharedPreferences",
          objective: "Store data locally in Flutter.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to SharedPreferences.</li>
                <li>Saving and retrieving key-value pairs.</li>
                <li>Managing user preferences.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Save user settings with SharedPreferences.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: SQLite Database in Flutter",
          objective: "Use SQLite for local data storage.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to SQLite with sqflite.</li>
                <li>Creating tables and inserting data.</li>
                <li>Querying and updating records.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a SQLite database for a to-do app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Animation Basics",
          objective: "Add animations to enhance UI.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Flutter animations.</li>
                <li>Implicit animations (AnimatedContainer).</li>
                <li>Basic tween animations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Animate a widget with AnimatedContainer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: Advanced Animations",
          objective: "Master advanced animation techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Custom animations with AnimationController.</li>
                <li>Hero animations for transitions.</li>
                <li>Staggered animations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a Hero animation between screens.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Dependency Injection",
          objective: "Implement dependency injection in Flutter.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is dependency injection?</li>
                <li>Using get_it or Provider for DI.</li>
                <li>Managing services and repositories.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up dependency injection for an app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Flutter Packages and Plugins",
          objective: "Leverage Flutter packages for functionality.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Finding and using pub.dev packages.</li>
                <li>Popular plugins (e.g., camera, maps).</li>
                <li>Customizing package integration.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Integrate a package (e.g., http) into an app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Testing in Flutter",
          objective: "Learn to test Flutter applications.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Unit, widget, and integration tests.</li>
                <li>Setting up the test environment.</li>
                <li>Writing test cases with flutter_test.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a unit test for a Dart function.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Debugging and Performance",
          objective: "Optimize and debug Flutter apps.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using DevTools for debugging.</li>
                <li>Performance profiling and optimization.</li>
                <li>Reducing app size and memory usage.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Profile an app with Flutter DevTools.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Firebase Integration – Basics",
          objective: "Integrate Firebase services into Flutter.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Firebase.</li>
                <li>Setting up Firebase in a Flutter project.</li>
                <li>Using Firebase Authentication.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add Firebase Authentication to an app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Firebase Realtime Database",
          objective: "Use Firebase Realtime Database.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Realtime Database overview.</li>
                <li>Reading and writing data.</li>
                <li>Listening to data changes.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a chat app with Realtime Database.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Firebase Cloud Firestore",
          objective: "Learn to use Cloud Firestore.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Cloud Firestore vs. Realtime Database.</li>
                <li>Queries and collections.</li>
                <li>Offline data synchronization.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a to-do app with Cloud Firestore.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Push Notifications with Firebase",
          objective: "Implement push notifications.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Firebase Cloud Messaging (FCM) basics.</li>
                <li>Setting up notifications in Flutter.</li>
                <li>Handling foreground and background messages.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add push notifications to an app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Flutter for Web and Desktop",
          objective: "Explore Flutter for web and desktop.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Flutter for web and desktop support.</li>
                <li>Configuring targets and building apps.</li>
                <li>UI adjustments for different platforms.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a simple web app with Flutter.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Internationalization and Localization",
          objective: "Support multiple languages in Flutter.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to internationalization.</li>
                <li>Using the intl package.</li>
                <li>Localizing text and formatting.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Localize an app for two languages.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Final Project – Planning",
          objective: "Plan the final Flutter project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>Suggested project ideas (e.g., to-do app, e-commerce).</li>
                <li>Planning features and design.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and wireframe.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Final Project – UI Design",
          objective: "Design the UI for the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Designing layouts and widgets.</li>
                <li>Applying themes and animations.</li>
                <li>Testing responsiveness.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build the project UI.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – State Management",
          objective: "Implement state management for the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Choosing a state management solution.</li>
                <li>Integrating Provider or BLoC.</li>
                <li>Managing app state effectively.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add state management to the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Final Project – API Integration",
          objective: "Integrate APIs into the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Connecting to REST APIs.</li>
                <li>Handling data with models.</li>
                <li>Displaying dynamic content.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Integrate an API into the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 31: Final Project – Local Storage",
          objective: "Add local storage to the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using SharedPreferences or SQLite.</li>
                <li>Saving and retrieving app data.</li>
                <li>Handling offline scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement local storage in the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 32: Final Project – Animations",
          objective: "Add animations to the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Enhancing UI with animations.</li>
                <li>Using Hero or custom animations.</li>
                <li>Testing animation performance.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add animations to the project UI.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 33: Final Project – Firebase Integration",
          objective: "Integrate Firebase into the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adding authentication or database.</li>
                <li>Using Cloud Firestore or Realtime Database.</li>
                <li>Implementing push notifications.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add Firebase to the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 34: Final Project – Testing",
          objective: "Test the project thoroughly.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Writing unit and widget tests.</li>
                <li>Testing API calls and state changes.</li>
                <li>Debugging issues.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write tests for the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 35: Final Project – Optimization",
          objective: "Optimize the project for performance.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Profiling with DevTools.</li>
                <li>Reducing app size and improving load times.</li>
                <li>Ensuring cross-platform compatibility.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Optimize the project performance.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 36: Final Project – Deployment Prep",
          objective: "Prepare the app for deployment.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Building APK and iOS apps.</li>
                <li>Configuring app icons and splash screens.</li>
                <li>Testing the release build.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Generate a release APK for Android.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 37: Final Project – Deployment to App Stores",
          objective: "Deploy the app to Google Play and App Store.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing app store assets (screenshots, descriptions).</li>
                <li>Uploading to Google Play Console.</li>
                <li>Submitting to Apple App Store.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Submit the app to Google Play (or simulate submission).</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 38: Final Project – Post-Deployment Review",
          objective: "Review and improve the deployed app.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Monitoring app performance post-deployment.</li>
                <li>Handling user feedback and updates.</li>
                <li>Planning future iterations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review deployment logs and plan an update.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 39: Introduction to Flutter Packages for Advanced Features",
          objective: "Explore advanced Flutter packages.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using packages for maps, camera, and payments.</li>
                <li>Integrating third-party APIs.</li>
                <li>Customizing package behavior.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add a map feature using a package.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 40: Flutter for IoT and Wearables",
          objective: "Explore Flutter for IoT and wearable devices.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Flutter for IoT and wearable apps.</li>
                <li>Connecting to devices via Bluetooth.</li>
                <li>Building a prototype app.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a basic IoT-connected app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 41: Accessibility in Flutter",
          objective: "Make apps accessible to all users.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Importance of accessibility.</li>
                <li>Implementing semantic labels and screen readers.</li>
                <li>Testing with accessibility tools.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add accessibility features to an app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 42: Continuous Integration for Flutter",
          objective: "Set up CI for Flutter apps.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to CI/CD for mobile apps.</li>
                <li>Using GitHub Actions or Codemagic.</li>
                <li>Automating builds and tests.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a CI pipeline for the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 43: Flutter Performance Tuning",
          objective: "Fine-tune app performance.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Optimizing widget trees.</li>
                <li>Reducing jank and improving frame rates.</li>
                <li>Using const constructors.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Optimize a Flutter app for performance.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 44: Final Project – Polish and Refinement",
          objective: "Polish the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adding final UI/UX touches.</li>
                <li>Fixing bugs and edge cases.</li>
                <li>Preparing documentation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Refine the project for presentation.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 45: Final Project – Presentation",
          objective: "Present the final Flutter project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a live demo or report.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Presenting to peers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Build cross-platform mobile apps with Flutter and Dart.</li>
            <li>Manage state and integrate APIs effectively.</li>
            <li>Deploy apps to app stores and optimize performance.</li>
            <li>Complete a fully functional Flutter project.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">To-Do App:</span> A task
              management app with local storage.
            </li>
            <li>
              <span className="font-bold text-sm">E-Commerce App:</span> A
              shopping app with API integration.
            </li>
            <li>
              <span className="font-bold text-sm">Social Media App:</span> A
              basic app with Firebase authentication and posts.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              Encourage practice with Flutter samples and pub.dev packages.
            </li>
            <li>Provide resources (e.g., Flutter docs, Dart tutorials).</li>
            <li>Use discussion time for Q&A and troubleshooting.</li>
          </ul>
          <p className="text-sm">
            This syllabus progresses from Flutter and Dart basics to advanced
            app development over 45 days.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-pink-600 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">Flutter Syllabus</h1>
        <p className="text-sm text-white">
          Master Mobile Development with Flutter (45 Hours – 1 Hour Per Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlutterApp;
